import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { 
  Dialog,
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "@/components/ui/dialog";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage,
  FormDescription
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Folder, Mail, Upload, X, FileText, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { candidateFoldersAPI, candidatesAPI2 } from "@/services/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

export default function AddCandidateModal({ isOpen, onClose, onSuccess, currentUser }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const resumeInputRef = useRef(null);
  const profileImageInputRef = useRef(null);

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      mobileNumber: "",
      address: "",
      folderId: "",
      sendWelcomeEmail: true
    }
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({
        name: "",
        email: "",
        mobileNumber: "",
        address: "",
        folderId: "",
        sendWelcomeEmail: true
      });
      setResumeFile(null);
      setProfileImageFile(null);
      setProfileImagePreview(null);
      fetchFolders();
    }
  }, [isOpen, form]);

  const fetchFolders = async () => {
    try {
      setLoadingFolders(true);
      const response = await candidateFoldersAPI.getAll();
      setFolders(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching folders:", error);
      toast({
        title: "Error",
        description: "Failed to load folders",
        variant: "destructive",
      });
    } finally {
      setLoadingFolders(false);
    }
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast({
          title: "Invalid File",
          description: "Only PDF files are allowed for resume",
          variant: "destructive",
        });
        e.target.value = null;
        return;
      }
      setResumeFile(file);
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid File",
          description: "Only image files are allowed for profile picture",
          variant: "destructive",
        });
        e.target.value = null;
        return;
      }
      setProfileImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeResume = () => {
    setResumeFile(null);
    if (resumeInputRef.current) {
      resumeInputRef.current.value = null;
    }
  };

  const removeProfileImage = () => {
    setProfileImageFile(null);
    setProfileImagePreview(null);
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = null;
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Validate resume is provided
      if (!resumeFile) {
        toast({
          title: "Resume Required",
          description: "Please upload a resume (PDF format only)",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('mobileNumber', data.mobileNumber || '');
      formData.append('address', data.address || '');
      formData.append('folderId', data.folderId || '');
      formData.append('sendWelcomeEmail', data.sendWelcomeEmail);
      
      // Append files
      if (resumeFile) {
        formData.append('resume', resumeFile);
      }
      if (profileImageFile) {
        formData.append('profileImage', profileImageFile);
      }

      const response = await candidatesAPI2.create(formData);
      
      toast({
        title: "Success",
        description: "Candidate created successfully",
      });
      
      onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error("Error creating candidate:", error);
      const errorMessage = error.response?.data?.message || 
                         (error.response?.data?.errors?.length 
                          ? error.response.data.errors[0].msg 
                          : "Failed to create candidate");
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[80vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Candidate</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
            <FormField
              control={form.control}
              name="name"
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter full name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              rules={{ 
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address"
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input 
                      type="email" 
                      placeholder="Enter email" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="mobileNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mobile Number</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter mobile number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter address" 
                      className="resize-none"
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

            <FormField
              control={form.control}
              name="folderId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Folder (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <div className="pt-[16px]">
                    <FormControl>
                      <SelectTrigger className="w-full h-10 px-3 text-sm">
                        <SelectValue placeholder="Select a folder or leave empty for Others" />
                      </SelectTrigger>
                    </FormControl>
                    </div>
                    <SelectContent>
                      {loadingFolders ? (
                        <SelectItem value="loading" disabled>
                          Loading folders...
                        </SelectItem>
                      ) : (
                        folders.map((folder) => (
                          <SelectItem key={folder._id} value={folder._id}>
                            <div className="flex items-center">
                              <div 
                                className="mr-2 h-3 w-3 rounded-full"
                                style={{ backgroundColor: folder.color }}
                              />
                              {folder.name}
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Resume Upload - Mandatory */}
            <div className="space-y-2">
              <FormLabel className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Resume <span className="text-red-500">*</span>
              </FormLabel>
              <FormDescription className="text-xs">
                Only PDF format is allowed
              </FormDescription>
              <div className="flex items-center gap-2">
                <Input
                  ref={resumeInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleResumeChange}
                  className="flex-1"
                />
                {resumeFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeResume}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {resumeFile && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  {resumeFile.name}
                </p>
              )}
            </div>

            {/* Profile Picture Upload - Optional */}
            <div className="space-y-2">
              <FormLabel className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Profile Picture (Optional)
              </FormLabel>
              <FormDescription className="text-xs">
                Upload a profile picture (JPG, PNG, etc.)
              </FormDescription>
              <div className="flex items-center gap-2">
                <Input
                  ref={profileImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfileImageChange}
                  className="flex-1"
                />
                {profileImageFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeProfileImage}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {profileImagePreview && (
                <div className="mt-2">
                  <img
                    src={profileImagePreview}
                    alt="Profile preview"
                    className="h-20 w-20 rounded-full object-cover border-2 border-gray-200"
                  />
                </div>
              )}
            </div>

            </div>

            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add Candidate
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}